/**
 * @file all-products-price-list-report.tsx
 * @module app/modals/report/all-products-price-list-report
 * @description Renders the all products price list report reporting modal, gathering report parameters and presenting or exporting the selected operational data.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Col, Row } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import CrossSvg from '../../../_admin/assets/media/close.png';
import { useEffect, useState } from 'react';
import { reports, seller } from '../../../api/apiEndPoints';
import APICallService from '../../../api/apiCallService';
import { CustomReportSelect } from '../../custom/Select/CustomReportSelect';
import { error } from '../../../Global/toast';
import { reportJson } from '../../../api/apiJSON/reports';
import { getKey } from '../../../Global/history';
import { warehouse as warehouseStoreString } from '../../../utils/storeString';
import { useWarehouse } from '../../context/WarehouseContext';
const ProductPriceListReport = (props: any) => {
  const { selectedWarehouse } = useWarehouse();
  const [fetchLoader, setFetchLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<any>(false);
  const [sellerData, setSellerData] = useState([]);
  const [totalSellers, setTotalSellers] = useState(0);
  const [selectedSeller, setSelectedSeller] = useState<any>([]);
  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await fetchSeller();
      setFetchLoader(false);
    })();
  }, []);
  const fetchSeller = async () => {
    let params: any = {
      pageNo: 1,
      limit: 0,
      sortKey: 'ownerName',
      sortOrder: 1,
      ['status[0]']: 4,
      needCount: true,
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      seller.listSeller,
      params,
      '',
      '',
      '',
      '',
      props.moduleName,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    setTotalSellers(response.total);
    setSellerData(response.records);
  };
  const handleSeller = async (event: any) => {
    setSelectedSeller(event);
    setValidation(false);
    if (!event.length) {
      setValidation(true);
    }
  };
  const handleSubmit = async () => {
    if (selectedSeller.length) {
      let sellers: any = [];
      selectedSeller.map((val: any) => {
        sellers.push(val.id);
      });
      let params: any = {
        businesses: sellers,
        utcOffset: new Date().getTimezoneOffset(),
      };
      if (selectedWarehouse?.value) {
        params.warehouse = selectedWarehouse?.value;
      }
      setLoading(true);
      let apiService = new APICallService(
        reports.productsPriceListReport,
        reportJson.getAllProductReport(params),
        undefined,
        'blob',
        '',
        '',
        props.moduleName,
        selectedWarehouse?.value || null
      );
      let response = await apiService.callAPI();
      if (response) {
        const pdfBlob = new Blob([response], { type: 'application/pdf' });
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(pdfBlob);
        downloadLink.download = 'product_price_list' + '.pdf';
        downloadLink.click();
        props.onHide();
      } else {
        error('Selected seller has no product listed');
        props.onHide();
      }
    } else {
      setValidation(true);
    }
    setLoading(false);
  };
  return (
    <>
      <Modal
        show={props.show}
        onHide={props.onHide}
        dialogClassName="modal-dialog-centered min-w-lg-690px"
        className="border-r10px"
        contentClassName="p-5"
        backdrop={loading ? 'static' : true}
      >
        <Modal.Header className="border-bottom-0 text-center pb-6 mx-auto">
          {!loading ? (
            <div className="symbol symbol-md-40px symbol-35px close-inner-top">
              <img
                width={40}
                height={40}
                src={CrossSvg}
                alt=""
                onClick={props.onHide}
              />
            </div>
          ) : (
            <></>
          )}
          <Modal.Title className="fs-30 fw-bolder mw-lg-350px">
            Download Product Price List
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0 pb-5">
          <Row className="align-items-center g-3">
            <Col md={12}>
              <div className="d-flex align-items-center mb-2">
                <div className="fs-18 fw-500">
                  Download Product Price List for{' '}
                </div>
              </div>
              <CustomReportSelect
                border={validation ? '#e55451' : '#e0e0df'}
                backgroundColor="#ffff"
                minHeight="60px"
                isLoading={fetchLoader}
                closeMenuOnSelect={false}
                isSearchable={true}
                options={
                  sellerData && sellerData.length
                    ? sellerData.map((val: any) => {
                        return {
                          label: (
                            <>
                              <div className="symbol symbol-30px border me-3">
                                <img
                                  src={val.image}
                                  className="object-fit-contain"
                                  alt=""
                                />
                              </div>
                              <span className="fs-16 fw-600 text-black mb-0">
                                {val.ownerName}
                              </span>
                            </>
                          ),
                          value: val._id,
                          id: val._id,
                          title: val.ownerName,
                        };
                      })
                    : []
                }
                text={'sellers selected'}
                hideSelectedOptions={false}
                onChange={(event: any) => {
                  handleSeller(event);
                }}
                // isClearable={selectedSeller ? true : false}
                isMulti={true}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="justify-content-center pt-0 border-top-0">
          <Button className="uw-btn uw-btn-primary uw-btn-lg"
            variant="primary"
            size="lg"
            onClick={() => {
              handleSubmit();
            }}
            disabled={loading}
          >
            {!loading && (
              <span className="indicator-label">
                Download Product Price List
              </span>
            )}
            {loading && (
              <span
                className="indicator-progress"
                style={{ display: 'block' }}
              >
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default ProductPriceListReport;
